import { useMutation, type UseMutationOptions, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import { ApiClient } from './api.client.js';
import {
  type Camera,
  CAMERA_ZONE_TYPES,
  type CameraVisionSettings,
  type CameraZone,
  type CameraZonePoint,
  type CameraZoneType,
  type CreateCamera,
} from '../models/index.js';

export { CAMERA_ZONE_TYPES };
export type { CameraZone, CameraZonePoint, CameraZoneType };

export const CamerasApiKeys = {
  GET_CAMERA_FOR_PATIENT: 'GET_CAMERA_FOR_PATIENT',
  GET_CAMERAS: 'GET_CAMERAS',
  GET_CAMERA: 'GET_CAMERA',
  CREATE_CAMERA: 'CREATE_CAMERA',
  UPDATE_CAMERA: 'UPDATE_CAMERA',
  DELETE_CAMERA: 'DELETE_CAMERA',
  TOGGLE_IR_LIGHT: 'TOGGLE_IR_LIGHT',
};

export class CamerasApi extends ApiClient {
  public static getCameras(patientId?: string): Promise<Camera[]> {
    return this.http.get(`/cameras`, { params: { patient: patientId } }).then((res) => res.data);
  }

  public static updateVisionSettings(
    cameraId: string,
    settings: CameraVisionSettings,
  ): Promise<{ visionSettings: CameraVisionSettings }> {
    return this.http.put(`/cameras/${cameraId}/vision-settings`, settings).then((res) => res.data);
  }

  public static getCamera(cameraId: string): Promise<Camera> {
    return this.http.get(`/cameras/${cameraId}`).then((res) => res.data);
  }

  public static createCamera(cameraDetails: CreateCamera): Promise<Camera> {
    return this.http.post('/cameras', cameraDetails);
  }

  public static updateCamera(cameraDetails: Partial<Camera>): Promise<void> {
    return this.http.put(`/cameras/${cameraDetails.id}`, cameraDetails);
  }

  public static deleteCamera(cameraId: string): Promise<void> {
    return this.http.delete(`/cameras/${cameraId}`);
  }

  public static updateZones(cameraId: string, zones: CameraZone[]): Promise<{ zones: CameraZone[] }> {
    return this.http
      .put<{ zones: CameraZone[] }>(`/cameras/${cameraId}/zones`, { zones })
      .then((res) => res.data);
  }

  public static toggleIrLight(
    cameraId: string,
    enabled: boolean,
  ): Promise<{ id: string; irCutFilter: boolean | null; irIlluminator: boolean | null }> {
    return this.http
      .post<{ id: string; irCutFilter: boolean | null; irIlluminator: boolean | null }>(
        `/cameras/${cameraId}/ir-light`,
        { enabled },
      )
      .then((res) => res.data);
  }
}

export function useCamerasForPatient(patientId?: string, queryOptions?: UseQueryOptions<Camera[]>) {
  return useQuery<Camera[]>([CamerasApiKeys.GET_CAMERA_FOR_PATIENT, patientId], () => CamerasApi.getCameras(patientId), queryOptions);
}

export function useAllCameras(queryOptions?: UseQueryOptions<Camera[]>) {
  return useQuery<Camera[]>([CamerasApiKeys.GET_CAMERAS], () => CamerasApi.getCameras(), queryOptions);
}

export function useCamera(cameraId: string, queryOptions?: UseQueryOptions<Camera>) {
  return useQuery<Camera>([CamerasApiKeys.GET_CAMERA, cameraId], () => CamerasApi.getCamera(cameraId), queryOptions);
}

export function useCreateCamera(mutationOptions?: UseMutationOptions<Camera, unknown, CreateCamera>) {
  return useMutation<Camera, unknown, CreateCamera>(
    [CamerasApiKeys.CREATE_CAMERA],
    (cameraDetails: CreateCamera) => CamerasApi.createCamera(cameraDetails),
    mutationOptions,
  );
}

export function useUpdateCamera(mutationOptions?: UseMutationOptions<void, unknown, Partial<Camera>>) {
  return useMutation<void, unknown, Partial<Camera>>(
    [CamerasApiKeys.UPDATE_CAMERA],
    (cameraDetails: Partial<Camera>) => CamerasApi.updateCamera(cameraDetails),
    mutationOptions,
  );
}

export function useDeleteCamera(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation<void, unknown, string>(
    [CamerasApiKeys.DELETE_CAMERA],
    (cameraId: string) => CamerasApi.deleteCamera(cameraId),
    mutationOptions,
  );
}

export function useToggleIrLight(
  cameraId: string,
  mutationOptions?: UseMutationOptions<
    { id: string; irCutFilter: boolean | null; irIlluminator: boolean | null },
    unknown,
    boolean
  >,
) {
  return useMutation<
    { id: string; irCutFilter: boolean | null; irIlluminator: boolean | null },
    unknown,
    boolean
  >(
    [CamerasApiKeys.TOGGLE_IR_LIGHT, cameraId],
    (enabled: boolean) => CamerasApi.toggleIrLight(cameraId, enabled),
    mutationOptions,
  );
}

export function useUpdateCameraZones(cameraId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (zones: CameraZone[]) => CamerasApi.updateZones(cameraId, zones),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERA, cameraId]);
        queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERAS]);
        queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERA_FOR_PATIENT]);
      },
    },
  );
}

export function useUpdateCameraVisionSettings(cameraId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (settings: CameraVisionSettings) => CamerasApi.updateVisionSettings(cameraId, settings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERA, cameraId]);
        queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERAS]);
        queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERA_FOR_PATIENT]);
      },
    },
  );
}
